import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, getDocs, doc, updateDoc, serverTimestamp, addDoc } from 'firebase/firestore';
import { onAuthStateChanged, getAuth } from 'firebase/auth';
import { db, auth } from '../firebase';
import '../styles/QuizSelection.css';
import { getCachedData, setCachedData } from '../utils/cache';

// Las funciones de traducción se movieron a nivel de módulo en la parte superior del archivo


const QuizSelection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [areas, setAreas] = useState([]);
  const [temas, setTemas] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');
  const [selectedArea, setSelectedArea] = useState(location.state?.selectedArea || null);
  const [isSharing, setIsSharing] = useState(false);
  const [sharingProgress, setSharingProgress] = useState(0);
  const [authUser, setAuthUser] = useState(null);
  const [authInitialized, setAuthInitialized] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(getAuth(), (u) => {
      setAuthUser(u);
      setAuthInitialized(true);
    });
    return unsubscribe;
  }, []);

  const fetchQuizData = useCallback(async () => {
    try {
      setLoading(true);

      // Importar Firebase dinámicamente
      const { getDatabase, ref, get } = await import('firebase/database');
      const { app } = await import('../firebase');

      const rtdb = getDatabase(app);

      // Cargar cuestionarios del usuario desde Firestore (siempre, antes del cache)
      let userQuizzesData = [];
      if (authUser) {
        try {
          const userQuizzesRef = collection(db, 'user_quizzes', authUser.uid, 'quizzes');
          const userQuizzesSnapshot = await getDocs(userQuizzesRef);
          userQuizzesData = userQuizzesSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            isCustom: true
          })).filter(q => !q.deleted);
          console.log(`[QuizSelection] 📊 Cargados ${userQuizzesData.length} cuestionarios personales.`);
          if (userQuizzesData.length > 0) {
            console.table(userQuizzesData.map(q => ({ id: q.id, name: q.name, areaId: q.areaId })));
          }
        } catch (e) {
          console.warn('[QuizSelection] Error cargando quizzes personales:', e.message);
        }
      }
      const userQuizzesAsTemas = userQuizzesData.map(q => ({
        id: q.id,
        nombre: q.name || q.temaName,
        area_id: parseInt(q.areaId) || 1,
        isCustom: true,
        questionCount: q.questionCount
      }));

      const specialtyQuizzesAsTemas = [];

      // Verificar versión remota para invalidación de caché
      let remoteVersion = null;
      try {
        const ccSnap = await get(ref(rtdb, 'cache_control/preguntas_updated'));
        if (ccSnap.exists()) remoteVersion = ccSnap.val();
      } catch {}

      // Intentar usar caché local
      const cachedAreas = getCachedData('areas');
      const cachedTemas = getCachedData('temas');
      const cachedPreguntas = getCachedData('preguntas_legacy');

      if (cachedAreas && cachedTemas && cachedPreguntas) {
        const versionOk = !remoteVersion || (
          cachedAreas.version === remoteVersion &&
          cachedTemas.version === remoteVersion &&
          cachedPreguntas.version === remoteVersion
        );
        if (versionOk) {
          console.log('[QuizSelection] 📦 Usando datos cacheados (localStorage)');
          setAreas(cachedAreas.data);
          const temasCache = cachedTemas.data.filter(t => t && t.nombre && t.area_id);

          // Mezclar con cuestionarios personales y de especialidad
          setTemas([...temasCache, ...userQuizzesAsTemas, ...specialtyQuizzesAsTemas]);
          setQuestions(cachedPreguntas.data);
          setLoading(false);
          return;
        }
      }

      // Cargar áreas desde Firebase
      const areasRef = ref(rtdb, 'areas');
      const areasSnapshot = await get(areasRef);
      let areasData = [];
      if (areasSnapshot.exists()) {
        const data = areasSnapshot.val();
        areasData = Object.entries(data).map(([id, area]) => ({
          id: parseInt(id),
          ...area
        }));
      }

      // Cargar temas desde Firebase
      const temasRef = ref(rtdb, 'temas');
      const temasSnapshot = await get(temasRef);
      let temasData = [];
      if (temasSnapshot.exists()) {
        const data = temasSnapshot.val();
        temasData = Object.entries(data).map(([id, tema]) => ({
          id: parseInt(id),
          ...tema
        }));
      }

      // Cargar preguntas desde Firebase (preguntas_por_tema)
      const preguntasPorTemaRef = ref(rtdb, 'preguntas_por_tema');
      const preguntasPorTemaSnap = await get(preguntasPorTemaRef);
      let preguntasData = [];
      if (preguntasPorTemaSnap.exists()) {
        const data = preguntasPorTemaSnap.val();
        for (const temaId of Object.keys(data)) {
          for (const qKey of Object.keys(data[temaId])) {
            const q = data[temaId][qKey];
            const qId = parseInt(qKey.replace('q_', ''));
            preguntasData.push({
              id: qId,
              ...q,
              tema_id: parseInt(temaId)
            });
          }
        }
      }

      // Guardar en caché local (solo si hay preguntas)
      setCachedData('areas', areasData, remoteVersion);
      setCachedData('temas', temasData, remoteVersion);
      if (preguntasData.length > 0) setCachedData('preguntas_legacy', preguntasData, remoteVersion);

      // Filtrar datos válidos
      const validAreas = areasData.filter(area => area && area.nombre);

      const temasFiltrados = temasData.filter(tema => tema && tema.nombre && tema.area_id);

      const validTemas = [...temasFiltrados, ...userQuizzesAsTemas, ...specialtyQuizzesAsTemas];
      const validQuestions = preguntasData.filter(q => q && q.pregunta);

      setAreas(validAreas);
      setTemas(validTemas);
      setQuestions(validQuestions);
      setError('');
    } catch (error) {
      console.error('Error cargando datos desde Firebase:', error);
      setError(error instanceof Error ? error.message : 'Error al cargar los datos desde Firebase');

      // Fallback con datos básicos si Firebase falla
      if (areas.length === 0) {
        setAreas([
          { id: 1, nombre: 'Conocimientos Marineros' },
          { id: 2, nombre: 'Legislación' },
          { id: 3, nombre: 'Conocimientos Específicos' },
          { id: 4, nombre: 'Oficiales' }
        ]);
        setTemas([
          { id: 1, nombre: 'Estructura del Buque', area_id: 1 },
          { id: 2, nombre: 'Medidas del Buque', area_id: 1 },
          { id: 3, nombre: 'Constitución Política', area_id: 2 },
          { id: 4, nombre: 'Ley Orgánica de la Armada', area_id: 2 },
          { id: 5, nombre: 'DAM Recursos Humanos', area_id: 3 },
          { id: 6, nombre: 'DAM Adiestramiento', area_id: 3 },
          { id: 7, nombre: 'Temas Avanzados', area_id: 4 }
        ]);
      }
    } finally {
      setLoading(false);
    }
  }, [authUser]);

  useEffect(() => {
    if (authInitialized) fetchQuizData();
  }, [fetchQuizData, authInitialized]);

  const getPreguntasCount = temaId => questions.filter(q => q.tema_id == temaId).length || 10;
  const getAreaTotalPreguntas = areaId => temas.filter(tema => tema.area_id === areaId).reduce((total, tema) => total + getPreguntasCount(tema.id), 0) || 30;
  const getAreaDescription = areaId => {
    switch (areaId) {
      case 1: return 'Sentido numérico, álgebra, geometría, trigonometría, probabilidad y estadística.';
      case 2: return 'Comprensión inferencial y crítica de textos científicos, literarios y periodísticos.';
      case 3: return 'Estructura discursiva, coherencia, gramática y normativa ortográfica del español.';
      case 4: return 'Lectura de textos cotidianos y uso del inglés en nivel B1 CENEVAL.';
      case 5: return 'Fundamentos empresariales, áreas funcionales y proceso administrativo.';
      case 6: return 'Aritmética básica y avanzada, razones, proporciones y problemas numéricos.';
      case 7: return 'Biología celular, molecular, genética, evolución y ecología.';
      case 8: return 'Límites, derivadas, integrales y sus aplicaciones en ciencias e ingeniería.';
      case 9: return 'Salud pública, epidemiología, anatomía humana y fisiología general.';
      case 10: return 'Fundamentos jurídicos, derecho constitucional, penal, civil y laboral.';
      case 11: return 'Microeconomía, macroeconomía, mercados, oferta y demanda.';
      case 12: return 'Lógica formal, epistemología, ética y filosofía contemporánea.';
      case 13: return 'Mecánica clásica, fluidos, termodinámica, ondas y electromagnetismo.';
      case 14: return 'Historia de México y procesos históricos universales contemporáneos.';
      case 15: return 'Géneros literarios, figuras retóricas y análisis de textos literarios.';
      case 16: return 'Interés simple, compuesto, anualidades y amortización financiera.';
      case 17: return 'Salud pública, anatomía, fisiología, biología celular y bioquímica médica.';
      case 18: return 'Estadística descriptiva, probabilidad clásica, distribuciones y muestreo.';
      case 19: return 'Bases cognitivas, desarrollo humano, personalidad y psicología social.';
      case 20: return 'Estructura atómica, enlaces, estequiometría, reacciones y soluciones químicas.';
      case 21: return 'Método científico, experimentación, física y química básica.';
      case 22: return 'Metodología social, instituciones y retos contemporáneos de la sociedad.';
      default: return 'Módulo disciplinar oficial del CENEVAL EXANI-II.';
    }
  };
  const getAreaIcon = areaId => {
    switch (areaId) {
      case 1: return '📐';
      case 2: return '📖';
      case 3: return '✍️';
      case 4: return '🌐';
      case 5: return '💼';
      case 6: return '🔢';
      case 7: return '🧬';
      case 8: return '📈';
      case 9: return '🩺';
      case 10: return '⚖️';
      case 11: return '📊';
      case 12: return '💡';
      case 13: return '⚡';
      case 14: return '🏛️';
      case 15: return '📚';
      case 16: return '💰';
      case 17: return '⚕️';
      case 18: return '🎲';
      case 19: return '🧠';
      case 20: return '🧪';
      case 21: return '🔬';
      case 22: return '👥';
      default: return '📘';
    }
  };
  const handleAreaSelect = areaId => setSelectedArea(areaId);
  const handleTemaSelect = tema => {
    // Unificación de UI: Todos los cuestionarios usan la misma interfaz de juego
    // Pasamos el areaId en el state para poder volver a la misma pantalla
    navigate(`/dashboard/quiz/${tema.id}`, { 
      state: { areaId: selectedArea } 
    });
  };
  const handleBackToAreas = () => setSelectedArea(null);

  const handleDeleteQuiz = async (e, quizId) => {
    e.stopPropagation(); // Evitar que inicie el quiz al borrar
    if (!await window.confirmAsync('¿Estás seguro de que quieres eliminar este cuestionario?')) return;

    try {
      setIsDeleting(true);
      const quizDocRef = doc(db, 'user_quizzes', auth.currentUser.uid, 'quizzes', quizId);
      await updateDoc(quizDocRef, {
        deleted: true,
        deletedAt: serverTimestamp()
      });

      // Actualizar estado local
      setTemas(prev => prev.filter(t => t.id !== quizId));
    } catch (error) {
      console.error('Error al eliminar cuestionario:', error);
      alert('Error al eliminar el cuestionario');
    } finally {
      setIsDeleting(false);
    }
  };

  // Las funciones de traducción se movieron a nivel de módulo en la parte superior del archivo

  const handleShareToVault = async (e, quiz) => {
    e.stopPropagation();
    if (!auth.currentUser) return;

    try {
      // Pedir código de tema
      const themeCode = prompt('Ingresa un código corto para el tema (ej: NAVEGACION, LEYES):', quiz.nombre?.split(':')[1]?.trim()?.substring(0, 10).toUpperCase() || '');
      if (!themeCode) return;

      const standardizedId = `EXANI.${themeCode.toUpperCase().replace(/\s/g, '_')}_${auth.currentUser.displayName?.replace(/\s/g, '_') || 'USUARIO'}`;

      if (!await window.confirmAsync(`¿Publicar en la Bóveda Técnica como "${standardizedId}"?`)) return;

      setIsSharing(true);
      setSharingProgress(10);

      // 3. Crear entrada en shared_vault/{uid}/quizzes
      const vaultData = {
        standardizedId,
        areaId: quiz.area_id || quiz.areaId,
        themeCode: themeCode.toUpperCase(),
        authorName: auth.currentUser.displayName || 'Usuario',
        authorId: auth.currentUser.uid,
        name: quiz.nombre || quiz.name,
        questionCount: quiz.questionCount,
        difficulty: quiz.difficulty || 'Media',
        timestamp: serverTimestamp(),
        isVault: true
      };

      const userVaultRef = collection(db, 'shared_vault', auth.currentUser.uid, 'quizzes');
      const vaultRef = await addDoc(userVaultRef, vaultData);
      setSharingProgress(30);

      // 4. Copiar preguntas
      const userQuestionsRef = collection(db, 'user_quizzes', auth.currentUser.uid, 'quizzes', quiz.id, 'questions');
      const userQuestionsSnap = await getDocs(userQuestionsRef);

      const vaultQuestionsRef = collection(db, 'shared_vault', auth.currentUser.uid, 'quizzes', vaultRef.id, 'questions');
      const totalQs = userQuestionsSnap.size;

      let processed = 0;
      for (const docSnap of userQuestionsSnap.docs) {
        const qData = docSnap.data();

        // Normalizar respuestas si es necesario (legacy 'opciones')
        const respuestasMap = {};
        if (qData.respuestas && typeof qData.respuestas === 'object' && !Array.isArray(qData.respuestas)) {
          // Ya está en formato nuevo
          Object.assign(respuestasMap, qData.respuestas);
        } else if (qData.opciones && Array.isArray(qData.opciones)) {
          // Legacy
          qData.opciones.forEach((opt, idx) => {
            const respId = 1000 + idx;
            respuestasMap[respId] = {
              id: respId,
              respuesta: opt,
              correcta: idx === (qData.correctIndex ?? qData.correctAnswer ?? 0) ? 1 : 0,
              preguntaId: 0
            };
          });
        }

        await addDoc(vaultQuestionsRef, {
          ...qData,
          respuestas: respuestasMap,
          addedAt: serverTimestamp()
        });

        processed++;
        setSharingProgress(30 + Math.floor((processed / totalQs) * 70));
      }

      setSharingProgress(100);
      setTimeout(() => {
        setIsSharing(false);
        setSharingProgress(0);
        alert('¡Cuestionario compartido exitosamente en la Bóveda Técnica!');
      }, 500);

    } catch (error) {
      console.error("Error sharing quiz:", error);
      alert(`Error al compartir: ${error.message}`);
      setIsSharing(false);
      setSharingProgress(0);
    }
  };

  if (loading) {
    return <div className="quiz-selection-container"><div className="loading">Cargando cuestionarios...</div></div>;
  }

  if (selectedArea !== null) {
    const area = areas.find(a => a.id === selectedArea);
    const temasDelArea = temas.filter(tema => tema.area_id === selectedArea);
    if (temasDelArea.length === 0) {
      return (
        <div className="quiz-selection-container">
          <button className="back-button" onClick={handleBackToAreas}>← Volver a Áreas</button>
          <h2>CUESTIONARIOS DE {area?.nombre || 'ÁREA SELECCIONADA'}</h2>
          <div className="error-message">No se encontraron temas para esta área. Por favor, selecciona otra área.</div>
        </div>
      );
    }
    return (
      <div className="quiz-selection-container">
        <button className="back-button" onClick={handleBackToAreas}>← Volver a Áreas</button>
        <h2>CUESTIONARIOS DE {area?.nombre}</h2>
        <div className="quiz-grid">
          {temasDelArea.map(tema => (
            <div
              className={`quiz-type ${tema.isCustom ? 'custom-quiz' : ''}`}
              key={tema.id}
              onClick={() => handleTemaSelect(tema)}
            >
              <div className="quiz-type-content">
                <div className="quiz-origin-tag">
                  {tema.isCustom ? '✨ Personalizado (IA)' : '📘 Oficial CENEVAL'}
                </div>
                <h3>{tema.nombre}</h3>
                <div className="quiz-footer">
                  <div className="quiz-stats">
                    {tema.isCustom ? tema.questionCount : getPreguntasCount(tema.id)} preguntas
                  </div>
                  {tema.isCustom && (
                    <div className="custom-actions">
                      <button
                        className="share-quiz-btn"
                        onClick={(e) => handleShareToVault(e, tema)}
                        disabled={isSharing || isDeleting}
                        title="Compartir en Bóveda Técnica"
                      >
                        🛡️
                      </button>
                      <button
                        className="delete-quiz-btn"
                        onClick={(e) => handleDeleteQuiz(e, tema.id)}
                        disabled={isDeleting || isSharing}
                        title="Eliminar cuestionario personal"
                      >
                        🗑️
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Overlay de Compartir */}
        {isSharing && (
          <div className="quiz-sharing-overlay">
            <div className="loading-spinner"></div>
            <h3>Compartiendo en Bóveda...</h3>
            <div className="quiz-sharing-progress-bar-track">
              <div
                className="quiz-sharing-progress-bar-fill"
                style={{ width: `${sharingProgress}%` }}
              ></div>
            </div>
            <p>{sharingProgress}%</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="quiz-selection-container">
      <h1>Selecciona un Área de Conocimiento</h1>
      {error && <div className="error-message">{error}</div>}
      <div className="quiz-grid">
        {areas.map(area => (
          <div className="area-card" key={area.id} onClick={() => handleAreaSelect(area.id)}>
            <div className="area-content">
              <div className="area-icon">{getAreaIcon(area.id)}</div>
              <h3>{area.nombre}</h3>
              <div className="area-stats">{getAreaTotalPreguntas(area.id)} preguntas</div>
              <p>{getAreaDescription(area.id)}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default QuizSelection; 